import { Helmet } from "react-helmet-async";

const SITE_URL = "https://medspadirectorypro.com";
const SITE_NAME = "MedSpa Directory Pro";
const DEFAULT_OG_IMAGE = "https://cdn.builder.io/api/v1/image/assets%2F2651dca913d34eafa320b193e1b2c34e%2Fcc449eaa12f04ec8a633e5d5dc5c0623";

interface PageMetaProps {
  title: string;
  description: string;
  path: string;
  ogImage?: string;
  ogType?: string;
  noIndex?: boolean;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  /** Preload the LCP image for this page (e.g. the home hero). Emitted only on
   *  the route that uses it, so other pages don't download an unused image. */
  preloadImage?: string;
  preloadImageSrcSet?: string;
  preloadImageSizes?: string;
}

export default function PageMeta({
  title,
  description,
  path,
  ogImage = DEFAULT_OG_IMAGE,
  ogType = "website",
  noIndex = false,
  jsonLd,
  preloadImage,
  preloadImageSrcSet,
  preloadImageSizes,
}: PageMetaProps) {
  const canonical = `${SITE_URL}${path}`;
  const fullTitle =
    path === "/" ? title : `${title} | ${SITE_NAME}`;

  const ldArray = jsonLd
    ? Array.isArray(jsonLd)
      ? jsonLd
      : [jsonLd]
    : [];

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />

      {noIndex && <meta name="robots" content="noindex, nofollow" />}

      {/* LCP image preload (route-scoped) */}
      {preloadImage && (
        <link
          rel="preload"
          as="image"
          href={preloadImage}
          {...(preloadImageSrcSet ? { imageSrcSet: preloadImageSrcSet } : {})}
          {...(preloadImageSizes ? { imageSizes: preloadImageSizes } : {})}
          type="image/webp"
          fetchPriority="high"
        />
      )}

      {/* Open Graph */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content={SITE_NAME} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* JSON-LD */}
      {ldArray.map((ld, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(ld)}
        </script>
      ))}
    </Helmet>
  );
}

export { SITE_URL, SITE_NAME, DEFAULT_OG_IMAGE };
